import React from 'react';
import { useNavigate } from 'react-router-dom';

function TermsOfService() {
  const navigate = useNavigate();

  const sections = [
    { id: 'overview', title: '1. Platform Overview' },
    { id: 'eligibility', title: '2. Eligibility' },
    { id: 'accounts', title: '3. User Accounts' },
    { id: 'workers', title: '4. Worker Terms' },
    { id: 'contractors', title: '5. Contractor Terms' },
    { id: 'bookings', title: '6. Bookings & Payments' },
    { id: 'cancellations', title: '7. Cancellation Policy' },
    { id: 'insurance', title: '8. Insurance & Liability' },
    { id: 'background', title: '9. Background Checks' },
    { id: 'reviews', title: '10. Reviews & Ratings' },
    { id: 'conduct', title: '11. Prohibited Conduct' },
    { id: 'termination', title: '12. Termination' },
    { id: 'disputes', title: '13. Dispute Resolution' },
    { id: 'governing', title: '14. Governing Law' },
    { id: 'changes', title: '15. Changes to Terms' },
    { id: 'contact', title: '16. Contact Information' },
  ];

  const content = {
    overview: {
      title: '1. Platform Overview',
      body: `TradePower is an online marketplace platform operated by TradePower Solutions Inc. that connects licensed skilled trade workers ("Workers") with general contractors, subcontractors, and construction businesses ("Contractors") for temporary, project-based, and short-term work engagements.

TradePower operates solely as a technology platform and marketplace. We do not employ Workers, supervise work performed, control job sites, or act as a staffing agency. TradePower facilitates connections between independent parties and provides tools for booking, communication, and scheduling.

By accessing or using TradePower, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use our platform.

These Terms were last updated on June 1, 2026 and are effective immediately upon account creation.`
    },
    eligibility: {
      title: '2. Eligibility',
      body: `To use TradePower, you must meet all of the following requirements:

- Be at least 18 years of age
- Be legally authorized to work in the United States
- Have a valid government-issued photo ID
- Provide accurate and truthful registration information
- Not have been previously banned or suspended from TradePower

Workers must additionally:
- Hold a valid driver's license in the state in which they intend to work
- Provide a driver's license number and state at registration, which must match their declared work state
- Possess any applicable trade licenses required by their state or municipality

Contractors must additionally:
- Represent a legitimate business entity or operate as a sole proprietor
- Maintain general liability insurance coverage appropriate for their operations
- Have the authority to hire and contract with independent workers

TradePower reserves the right to verify eligibility requirements at any time and to suspend or terminate accounts that do not meet these requirements.`
    },
    accounts: {
      title: '3. User Accounts',
      body: `When you create an account on TradePower, you agree to:

- Provide accurate, current, and complete information
- Maintain and promptly update your account information
- Keep your password secure and confidential
- Notify TradePower immediately of any unauthorized use of your account
- Be responsible for all activities that occur under your account

You may not create multiple accounts, share your account with others, or create an account on behalf of someone else without their express permission.

TradePower reserves the right to suspend or terminate any account at our sole discretion, including for violations of these Terms, providing false information, or engaging in fraudulent or harmful conduct.

Your account information, including your driver's license state and work state, is used for verification purposes. Workers acknowledge that providing false state information is a violation of these Terms and may result in immediate account termination.`
    },
    workers: {
      title: '4. Worker Terms',
      body: `INDEPENDENT CONTRACTOR STATUS

Workers on TradePower are independent contractors, not employees of TradePower or of the Contractors who hire them. By registering as a Worker, you expressly acknowledge and agree that:

- You are operating as an independent contractor (1099)
- You are responsible for all federal, state, and local taxes on income earned through TradePower, including self-employment taxes
- You are required to provide a W-9 form and tax identification information upon request
- TradePower will issue 1099 forms as required by law for payments processed through the platform
- You are not entitled to employment benefits including health insurance, workers' compensation, unemployment insurance, or retirement benefits from TradePower or from Contractors

WORKER RESPONSIBILITIES

As a Worker, you agree to:

- Accurately represent your skills, licenses, certifications, and experience
- Only accept jobs for which you are qualified and properly licensed
- Arrive on time and fulfill all accepted job commitments
- Conduct yourself professionally on all job sites
- Comply with all applicable federal, state, and local laws and regulations
- Obtain and maintain any required licenses or certifications for your trade
- Carry appropriate personal insurance coverage

RELIABILITY AND CONDUCT

TradePower tracks the following worker behaviors which are visible to Contractors:

- Late cancellations (cancellations within 48 hours of job start time)
- No-shows (failure to appear for a scheduled job)
- Response time to job requests

Workers with poor reliability statistics may receive fewer job opportunities on the platform. TradePower does not remove accurate reliability statistics from worker profiles.`
    },
    contractors: {
      title: '5. Contractor Terms',
      body: `CONTRACTOR RESPONSIBILITIES

As a Contractor, you agree to:

- Accurately represent your company, licenses, and insurance coverage
- Provide safe working conditions on all job sites
- Treat Workers with respect and professionalism
- Pay for services rendered as agreed through the platform
- Comply with all applicable labor laws, safety regulations, and building codes
- Verify that Workers have the appropriate licenses for the work being performed

EMPLOYER OF RECORD

Contractors acknowledge that:

- Workers booked through TradePower are independent contractors, not employees
- Contractors are NOT responsible for Workers' taxes, benefits, or employment law obligations
- Contractors are responsible for ensuring their job sites comply with all OSHA and applicable safety standards
- Contractors assume full responsibility for the direction, control, and supervision of work performed on their job sites

INSURANCE OBLIGATIONS

Contractors who acknowledge that their general liability insurance covers 1099 subcontractors on their job sites represent that this is true and accurate. Contractors are solely responsible for ensuring their insurance coverage is adequate and current. TradePower does not verify insurance coverage and assumes no liability related to insurance claims.`
    },
    bookings: {
      title: '6. Bookings & Payments',
      body: `PLATFORM FEE

TradePower charges a platform service fee on all completed bookings. This fee is incorporated into the rate displayed to Contractors and is not separately itemized. Workers receive their stated hourly rate in full.

The current platform fee structure is subject to change with notice to users.

MINIMUM BOOKING

All bookings through TradePower have a minimum duration of four (4) hours per day.

PAYMENT PROCESSING

Payments are processed through TradePower's payment processor. By using the platform, you agree to the terms of our payment processor. TradePower is not responsible for payment processing errors caused by third-party payment providers.

WORKER PAYOUTS

Workers will receive payment for completed and verified work days within the standard payout schedule as specified in the payment section of your account dashboard. Payout schedules may vary based on your location and payout method selected.

TAXES

Workers are solely responsible for all tax obligations arising from income earned through TradePower. TradePower will provide applicable tax documentation as required by law. Contractors are responsible for ensuring compliance with any tax withholding obligations applicable to their use of independent contractors under applicable law.

ESTIMATED TOTALS

Job totals displayed on TradePower are estimates based on booked hours and days. Final amounts may vary due to schedule changes, modifications, or cancellations. TradePower is not responsible for discrepancies between estimates and final amounts.`
    },
    cancellations: {
      title: '7. Cancellation Policy',
      body: `CONTRACTOR CANCELLATIONS — PENDING JOBS

Contractors may cancel a job request that has not yet been accepted by the Worker at any time without penalty.

CONTRACTOR CANCELLATIONS — ACCEPTED JOBS

When a Contractor cancels a job that has already been accepted by a Worker:

- A cancellation penalty of 25% of the estimated total job value will be charged
- The remaining 75% will be refunded to the Contractor
- The Worker will receive a cancellation notification

This policy exists to protect Workers who have committed to a job, blocked their availability, and may have declined other work opportunities.

WORKER CANCELLATIONS

Workers may cancel an accepted job at any time. Workers acknowledge:

- Cancellations made more than 48 hours before the scheduled job start time are free of financial penalty
- Cancellations made within 48 hours of the scheduled job start time ("Late Cancellations") will be recorded as a Late Cancellation on the Worker's profile
- Late Cancellation statistics are visible to Contractors browsing the platform
- Repeated Late Cancellations may result in reduced visibility or account suspension

WORKER NO-SHOWS

If a Worker fails to appear for a scheduled job without cancelling, the Contractor may mark the job as a No-Show. No-Show records are permanently recorded on the Worker's profile and are visible to all Contractors.

TradePower reserves the right to suspend or terminate accounts with excessive Late Cancellations or No-Shows.

FORCE MAJEURE

Cancellations due to verified emergencies, natural disasters, or circumstances beyond a party's reasonable control may be reviewed by TradePower on a case-by-case basis.`
    },
    insurance: {
      title: '8. Insurance & Liability',
      body: `INSURANCE DISCLAIMER

TradePower does not verify insurance coverage of any kind and assumes no liability for work performed on job sites. Workers and Contractors are solely responsible for their own insurance coverage. TradePower is a marketplace platform only and is not responsible for any injuries, damages, property damage, or losses that occur during the performance of work arranged through this platform.

WORKER INSURANCE

Workers are responsible for carrying any insurance they deem appropriate, including but not limited to general liability insurance and occupational accident insurance. Workers who carry their own general liability insurance may upload proof of insurance to their profile, which may be displayed as an informational badge. TradePower does not verify the accuracy or currency of uploaded insurance documents.

CONTRACTOR INSURANCE

Contractors who indicate on their profile that their general liability insurance covers 1099 subcontractors on their job sites are solely responsible for the accuracy of that representation. TradePower does not verify contractor insurance and accepts no liability for any insurance claims or disputes.

LIMITATION OF LIABILITY

TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, TRADEPOWER SOLUTIONS INC. SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF (OR INABILITY TO ACCESS OR USE) THE PLATFORM.

TRADEPOWER SOLUTIONS INC.'S TOTAL LIABILITY FOR ANY CLAIM ARISING FROM OR RELATING TO THESE TERMS OR YOUR USE OF THE PLATFORM SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT PAID BY YOU TO TRADEPOWER IN THE TWELVE (12) MONTHS PRIOR TO THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100.00).`
    },
    background: {
      title: '9. Background Checks',
      body: `OPTIONAL BACKGROUND CHECK SERVICE

TradePower offers an optional background check service that Contractors may purchase for an additional fee at the time of booking. Background checks are currently priced at $39.99 per check and are subject to change.

BACKGROUND CHECK PROVIDERS

Background checks are conducted by third-party background check providers. By consenting to a background check, Workers agree to the terms and conditions of TradePower's background check provider in addition to these Terms.

WORKER CONSENT

Workers who register on TradePower acknowledge that they consent to a background check being conducted prior to their profile going live on the platform, and may be subject to additional background checks at the request of Contractors who book their services.

BACKGROUND CHECK RESULTS

TradePower and its background check provider comply with the Fair Credit Reporting Act (FCRA) and applicable state laws regarding the use of background check information. Workers have the right to dispute inaccurate background check information directly with the background check provider.

TradePower reserves the right to suspend or remove Worker profiles based on background check results, in accordance with applicable law.

LIMITATIONS

Background checks do not guarantee the suitability, safety, or reliability of any Worker. TradePower makes no representations about the completeness or accuracy of background check results.`
    },
    reviews: {
      title: '10. Reviews & Ratings',
      body: `REVIEW SYSTEM

Upon completion of a job, both Workers and Contractors may leave reviews and ratings for each other. Reviews become available once the job end date has passed and the job status is accepted.

REVIEW INTEGRITY

By leaving a review, you agree that:

- Your review reflects your genuine, first-hand experience with the other party
- You will not post false, misleading, defamatory, or retaliatory reviews
- You will not offer or accept compensation in exchange for positive reviews
- You will not use the review system to harass or intimidate other users

REVIEW POLICY

TradePower reserves the right to remove reviews that violate these Terms, including reviews that are fraudulent, abusive, or otherwise inappropriate. Removed reviews will not restore the party's previous rating.

Each party may leave one review per job. Reviews cannot be edited after submission.

RATING IMPACT

Worker ratings are calculated as a rolling average of all reviews received and are displayed on Worker profiles and search results. Contractor ratings are similarly calculated and displayed.

TradePower does not manipulate or artificially inflate ratings. Ratings reflect the actual reviews submitted by verified users of the platform.`
    },
    conduct: {
      title: '11. Prohibited Conduct',
      body: `The following conduct is strictly prohibited on TradePower:

FRAUD AND MISREPRESENTATION
- Providing false information during registration or profile setup
- Misrepresenting your qualifications, licenses, or experience
- Creating fake reviews or manipulating ratings
- Impersonating another person or entity

PLATFORM ABUSE
- Circumventing the platform to avoid TradePower fees by arranging direct payment outside the platform for jobs originated on TradePower
- Creating multiple accounts
- Scraping, copying, or distributing platform data without authorization
- Using bots or automated tools to access the platform

HARASSMENT AND DISCRIMINATION
- Harassing, threatening, or intimidating other users
- Discriminating against Workers or Contractors based on race, color, religion, sex, national origin, age, disability, or any other protected characteristic
- Using offensive, abusive, or inappropriate language in messages or reviews

ILLEGAL ACTIVITY
- Using the platform to arrange work that violates applicable laws or regulations
- Submitting false claims for work not performed
- Misrepresenting business licenses or insurance coverage
- Engaging in tax fraud or misreporting income

Violations of these prohibitions may result in immediate account suspension or termination, removal of reviews or ratings, legal action, or reporting to appropriate authorities.`
    },
    termination: {
      title: '12. Termination',
      body: `TERMINATION BY USER

You may terminate your TradePower account at any time by contacting support or using the account deletion feature in your settings. Upon termination:

- Your profile will be removed from public visibility
- Pending bookings will be cancelled in accordance with the Cancellation Policy
- You will remain responsible for any outstanding obligations, including payment for services rendered

TERMINATION BY TRADEPOWER

TradePower Solutions Inc. may suspend or terminate your account at any time, with or without notice, for:

- Violation of these Terms of Service
- Providing false or misleading information
- Engaging in fraudulent, abusive, or illegal conduct
- Repeated Late Cancellations, No-Shows, or poor reliability
- Any conduct that TradePower determines is harmful to the platform or its users

EFFECT OF TERMINATION

Upon termination, your right to access and use the platform immediately ceases. TradePower may retain certain information as required by law or for legitimate business purposes. Reviews left by other parties about you may remain on the platform even after your account is terminated.

TradePower Solutions Inc. is not liable to you or any third party for termination of your account in accordance with these Terms.`
    },
    disputes: {
      title: '13. Dispute Resolution',
      body: `INFORMAL RESOLUTION

Before initiating any formal dispute, you agree to contact TradePower support and attempt to resolve the dispute informally. TradePower will make good-faith efforts to resolve disputes between users.

ARBITRATION AGREEMENT

PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS.

Except as provided below, you and TradePower Solutions Inc. agree that any dispute, claim, or controversy arising from or relating to these Terms or your use of the platform will be resolved by binding individual arbitration, rather than in court.

Arbitration will be conducted by a neutral arbitrator in accordance with the American Arbitration Association (AAA) Consumer Arbitration Rules. The arbitration will take place in Mecklenburg County, North Carolina, or by telephone at either party's request.

CLASS ACTION WAIVER

YOU AND TRADEPOWER SOLUTIONS INC. AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE ACTION.

EXCEPTIONS

Nothing in this arbitration agreement prevents either party from seeking emergency injunctive relief in court to prevent irreparable harm.`
    },
    governing: {
      title: '14. Governing Law',
      body: `These Terms of Service are governed by and construed in accordance with the laws of the State of North Carolina, without regard to its conflict of law provisions.

Any legal proceedings not subject to arbitration under Section 13 shall be brought exclusively in the state or federal courts located in Mecklenburg County, North Carolina. You consent to the personal jurisdiction of such courts.

TradePower Solutions Inc. operates in compliance with applicable federal and North Carolina state laws governing independent contractor relationships, marketplace platforms, and digital services.

If you access TradePower from outside the United States, you are responsible for compliance with local laws to the extent applicable. TradePower makes no representation that the platform is appropriate or available for use in all locations.`
    },
    changes: {
      title: '15. Changes to Terms',
      body: `TradePower Solutions Inc. reserves the right to modify these Terms of Service at any time. We will provide notice of material changes by:

- Posting the updated Terms on the platform
- Sending an email notification to your registered email address
- Displaying a notice within the platform upon your next login

Your continued use of TradePower after the effective date of any changes constitutes your acceptance of the updated Terms. If you do not agree to the updated Terms, you must stop using the platform and may terminate your account.

We encourage you to review these Terms periodically to stay informed of any updates. The date of the most recent update is displayed at the top of this document.

Minor changes such as corrections of typographical errors, clarifications, or reorganization of content may be made without notice.`
    },
    contact: {
      title: '16. Contact Information',
      body: `If you have questions about these Terms of Service, please contact us:

TradePower Solutions Inc.
Charlotte, North Carolina

Email: legal@tradepowerapp.com
Support: support@tradepowerapp.com

For urgent account issues, please contact our support team through the in-app messaging system or at the email address above.

For disputes, please first attempt informal resolution by contacting support@tradepowerapp.com before initiating arbitration or other legal proceedings.

TradePower Solutions Inc.'s registered agent information is available through the North Carolina Secretary of State's office.`
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
          TradePower
        </h1>
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>← Back</button>
      </div>

      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title}>Terms of Service</h1>
          <p style={styles.lastUpdated}>Last updated: June 1, 2026</p>
          <p style={styles.intro}>
            Please read these Terms of Service carefully before using TradePower. By creating
            an account or using our platform, you agree to be bound by these Terms.
          </p>
        </div>

        <div style={styles.layout}>
          <div style={styles.toc}>
            <h3 style={styles.tocTitle}>Table of Contents</h3>
            {sections.map(section => (
              <a key={section.id} href={`#${section.id}`} style={styles.tocLink}>
                {section.title}
              </a>
            ))}
            <div style={styles.tocDivider} />
            <a href="/privacy" style={styles.tocLinkAlt}
              onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>
              Privacy Policy →
            </a>
          </div>

          <div style={styles.sections}>
            {sections.map(section => (
              <div key={section.id} id={section.id} style={styles.section}>
                <h2 style={styles.sectionTitle}>{content[section.id].title}</h2>
                <div style={styles.sectionBody}>
                  {content[section.id].body.split('\n\n').map((paragraph, i) => {
                    if (paragraph.startsWith('•')) {
                      const items = paragraph.split('\n').filter(l => l.trim());
                      return (
                        <ul key={i} style={styles.list}>
                          {items.map((item, j) => (
                            <li key={j} style={styles.listItem}>{item.replace('• ', '')}</li>
                          ))}
                        </ul>
                      );
                    }
                    if (paragraph === paragraph.toUpperCase() && paragraph.length < 80) {
                      return <h4 key={i} style={styles.subheading}>{paragraph}</h4>;
                    }
                    return <p key={i} style={styles.paragraph}>{paragraph}</p>;
                  })}
                </div>
              </div>
            ))}

            <div style={styles.footer}>
              <p style={styles.footerText}>
                By using TradePower, you acknowledge that you have read, understood, and agree
                to be bound by these Terms of Service.
              </p>
              <div style={styles.footerLinks}>
                <button style={styles.footerBtn} onClick={() => navigate('/privacy')}>
                  Privacy Policy
                </button>
                <button style={styles.footerBtn} onClick={() => navigate('/dashboard')}>
                  Return to TradePower
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  navbar: {
    backgroundColor: 'white', padding: '16px 32px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)', position: 'sticky', top: 0, zIndex: 100,
  },
  logo: { fontSize: '24px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  backButton: {
    padding: '8px 16px', backgroundColor: 'transparent',
    color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
  },
  content: { maxWidth: '1100px', margin: '0 auto', padding: '40px 24px' },
  header: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '32px',
    marginBottom: '24px', textAlign: 'center',
  },
  title: { fontSize: '32px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' },
  lastUpdated: { fontSize: '14px', color: '#999', marginBottom: '16px' },
  intro: { fontSize: '15px', color: '#555', lineHeight: '1.6', maxWidth: '700px', margin: '0 auto' },
  layout: { display: 'grid', gridTemplateColumns: '240px 1fr', gap: '24px', alignItems: 'start' },
  toc: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '20px',
    position: 'sticky', top: '80px',
  },
  tocTitle: {
    fontSize: '13px', fontWeight: 'bold', color: '#1a1a2e',
    marginBottom: '12px', textTransform: 'uppercase',
  },
  tocLink: {
    display: 'block', fontSize: '13px', color: '#555', textDecoration: 'none',
    padding: '5px 0', borderBottom: '1px solid #f0f0f0', lineHeight: '1.4',
  },
  tocLinkAlt: {
    display: 'block', fontSize: '13px', color: '#E94560',
    textDecoration: 'none', padding: '5px 0', fontWeight: 'bold',
  },
  tocDivider: { height: '1px', backgroundColor: '#e0e0e0', margin: '12px 0' },
  sections: { display: 'flex', flexDirection: 'column', gap: '16px' },
  section: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '28px',
  },
  sectionTitle: {
    fontSize: '20px', fontWeight: 'bold', color: '#1a1a2e',
    marginBottom: '16px', borderBottom: '2px solid #E94560', paddingBottom: '8px',
  },
  sectionBody: {},
  paragraph: { fontSize: '14px', color: '#444', lineHeight: '1.8', marginBottom: '14px' },
  subheading: {
    fontSize: '13px', fontWeight: 'bold', color: '#1a1a2e',
    marginTop: '16px', marginBottom: '8px', letterSpacing: '0.5px',
  },
  list: { paddingLeft: '20px', marginBottom: '14px' },
  listItem: { fontSize: '14px', color: '#444', lineHeight: '1.8', marginBottom: '4px' },
  footer: {
    backgroundColor: '#1a1a2e', borderRadius: '12px', padding: '28px', textAlign: 'center',
  },
  footerText: { fontSize: '14px', color: '#aaa', lineHeight: '1.6', marginBottom: '20px' },
  footerLinks: { display: 'flex', gap: '12px', justifyContent: 'center' },
  footerBtn: {
    padding: '10px 20px', backgroundColor: 'transparent', color: 'white',
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px',
  },
};

export default TermsOfService;